import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import { EditActions, editActions } from "../../actions/editActions";
import { hasWriteAccess, nameofFactory } from "../../utils/utils";
import { driveSignalGroupHasChildren } from "../../utils/pdApiUtils";
import Editor from "../../components/Editing/Editor";
import NumberField from "../../components/Editing/NumberField";
import TextField from "../../components/Editing/TextField";
import { Divider, Modal } from "semantic-ui-react";
import ButtonField from "../../components/Editing/ButtonField";
import { EditLocalizationStatus } from "./EditLocalizationStatus";
import TextIndicatorField from "../../components/Editing/TextIndicatorField";

type StateProps = {
  editedDriveSignalGroup: Api.DriveSignalGroup;
  storedDriveSignalGroup: Api.DriveSignalGroup;
  pdApi: State.PdApi;
  activeUser?: Api.User;
  columns: State.ColumnMap;
  isShortenTextEnabled: boolean;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
};

export type EditDriveSignalGroupProps = StateProps & DispatchProps & OwnProps;

export type EditDriveSignalGroupState = {
  isEditLocalizationStatusOpen: boolean;
  locsOriginal: Api.LocsDriveSignalGroup[];
  locsEdited: Api.LocsDriveSignalGroup[];
};

const driveSignalGroupFields = nameofFactory<Api.DriveSignalGroup>();

export class EditDriveSignalGroup extends React.Component<
  EditDriveSignalGroupProps,
  EditDriveSignalGroupState
> {
  constructor(props: EditDriveSignalGroupProps) {
    super(props);
    this.state = {
      isEditLocalizationStatusOpen: false,
      locsOriginal: [],
      locsEdited: []
    };
  }
  public render(): JSX.Element {
    const { isEditLocalizationStatusOpen } = this.state;
    const {
      editedDriveSignalGroup,
      storedDriveSignalGroup,
      activeUser,
      columns,
      pdApi,
      toggleModal,
      isShortenTextEnabled
    } = this.props;
    const { signals } = pdApi;

    const isNew = !storedDriveSignalGroup;
    const permissions = hasWriteAccess(activeUser, "Drive");
    const deleteDisabled =
      storedDriveSignalGroup &&
      driveSignalGroupHasChildren(storedDriveSignalGroup.enumValue, signals);

    return (
      <Editor
        itemName={"drive signal group"}
        originalObject={storedDriveSignalGroup}
        editedObject={editedDriveSignalGroup}
        hasWritePermission={hasWriteAccess(activeUser, "Drive")}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => {
          this.props.editActions.setEdited({
            ...editedDriveSignalGroup,
            locs: this.state.locsEdited
          });
          this.props.editActions.update("signalGroups");
        }}
        create={() => {
          this.props.editActions.setEdited({
            ...editedDriveSignalGroup,
            locs: this.state.locsEdited
          });
          this.props.editActions.add("signalGroups");
        }}
        copy={() => {
          this.props.editActions.setEdited({
            ...editedDriveSignalGroup,
            locs: this.state.locsEdited
          });
          this.props.editActions.startEditing(editedDriveSignalGroup, true);
        }}
        remove={{
          callback: toggleModal,
          disabledTooltip: " as it is used in one or more Drive Signals.",
          disabled: deleteDisabled
        }}
      >
        <NumberField
          id="enumValue"
          label="Enum Value"
          value={editedDriveSignalGroup.enumValue}
          isError={!this.isFieldValid(driveSignalGroupFields("enumValue"))}
          errorMessage="The enum value must be unique."
          readonly={!isNew}
          onChange={enumValue =>
            this.props.editActions.setEdited({ ...editedDriveSignalGroup, enumValue })
          }
        />
        <TextField
          id="enumName"
          label="Enum Name"
          value={editedDriveSignalGroup.enumName}
          isError={!this.isFieldValid(driveSignalGroupFields("enumName"))}
          errorMessage={
            !this.isEmptyString(editedDriveSignalGroup.enumName)
              ? "The enum name must be unique."
              : ""
          }
          onChange={enumName =>
            this.props.editActions.setEdited({
              ...editedDriveSignalGroup,
              enumName: enumName.trim()
            })
          }
          readonly={!permissions}
        />
        <TextField
          id="description"
          label="Description"
          value={editedDriveSignalGroup.description}
          isError={!this.isFieldValid(driveSignalGroupFields("description"))}
          onChange={description =>
            this.props.editActions.setEdited({
              ...editedDriveSignalGroup,
              description: description.trim()
            })
          }
          readonly={!permissions}
        />
        <Divider horizontal={true}>{"English translations"}</Divider>
        <TextIndicatorField
          id={"needsUpdateIndicator"}
          text={"Edited texts will be translated."}
          isWarning={this.englishTranslationsChanged()}
        />
        <TextField
          id="englishName"
          label="English Name"
          value={editedDriveSignalGroup.name}
          isError={!this.isFieldValid(driveSignalGroupFields("name"))}
          onChange={value => {
            this.props.editActions.setEdited({
              ...editedDriveSignalGroup,
              name: value.trim()
            } as Api.DriveSignalGroup);
          }}
          readonly={!permissions}
        />
        <ButtonField
          id={"viewTranslationsButton"}
          label="View translations"
          onButtonClick={this.toggleEditLocalizationStatusModal}
        />
        <Modal size="large" open={isEditLocalizationStatusOpen} className="data-sections-modal">
          <EditLocalizationStatus
            itemType={"SignalGroup"}
            editedItem={{ ...editedDriveSignalGroup, locs: this.state.locsEdited }}
            getOption="signalGroupLocalizations"
            columns={columns["Drive Signal Group Localization Status"]}
            pdApi={pdApi}
            toggleEditLocalizationStatusModal={this.toggleEditLocalizationStatusModal}
            setLocalizationsEdited={this.setLocalizationsEdited}
            isShortenTextEnabled={isShortenTextEnabled}
            englishTranslationChanged={this.englishTranslationsChanged()}
            userPermissions={permissions}
            locsOriginal={this.state.locsOriginal || []}
            setLocalizationsNonEdited={this.setLocalizationsNonEdited}
          />
        </Modal>
      </Editor>
    );
  }

  private englishTranslationsChanged = (): boolean =>
    this.props.editedDriveSignalGroup.name !== this.props.storedDriveSignalGroup?.name;

  private handleEnglishTranslationsChange(item: Api.DriveSignalGroup) {
    const { storedDriveSignalGroup, editedDriveSignalGroup } = this.props;
    const { locsOriginal, locsEdited } = this.state;

    // create both edited and stored item locs if needed
    const edited =
      locsEdited.length === 0
        ? [
            {
              text: item.name,
              localeNavigation: { locale: "en-US" }
            } as Api.LocsDriveSignal
          ]
        : this.state.locsEdited.map(loc => {
            if (loc.localeNavigation.locale === "en-US") {
              return {
                ...loc,
                text: item.name
              } as Api.LocsDriveSignal;
            } else {
              return loc;
            }
          });
    const original =
      locsOriginal.length === 0
        ? [
            {
              text: storedDriveSignalGroup?.name,
              localeNavigation: { locale: "en-US" }
            } as Api.LocsDriveSignal
          ]
        : locsOriginal;

    this.setState((prevState: EditDriveSignalGroupState) => ({
      ...prevState,
      locsEdited: edited,
      locsOriginal: original
    }));
    this.props.editActions.setEdited({
      ...editedDriveSignalGroup,
      name: item.name
    });
  }

  private toggleEditLocalizationStatusModal = (): void => {
    this.setState((prevState: EditDriveSignalGroupState) => ({
      ...prevState,
      isEditLocalizationStatusOpen: !prevState.isEditLocalizationStatusOpen
    }));
  };

  private setLocalizationsEdited = (
    newLocalizations: Api.LocsDriveSignalGroup[],
    oldLocalizations: Api.LocsDriveSignalGroup[]
  ): void => {
    const { editedDriveSignalGroup } = this.props;
    this.setState((prevState: EditDriveSignalGroupState) => ({
      ...prevState,
      locsEdited: newLocalizations,
      locsOriginal: oldLocalizations
    }));
    this.props.editActions.setEdited({
      ...editedDriveSignalGroup,
      name:
        newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")?.text ||
        editedDriveSignalGroup?.name,
      locs: newLocalizations
    } as Api.DriveSignalGroup);
  };

  private setLocalizationsNonEdited = (Localizations: Api.LocsDriveSignalGroup[]): void => {
    this.setState((prevState: EditDriveSignalGroupState) => ({
      ...prevState,
      locsOriginal: Localizations
    }));
  };

  private isFieldValid = (field: string): boolean => {
    const { editedDriveSignalGroup, storedDriveSignalGroup, pdApi } = this.props;
    const isNew = !storedDriveSignalGroup;
    if (!Number.isInteger(editedDriveSignalGroup.enumValue)) return false;
    switch (field) {
      case driveSignalGroupFields("enumValue"):
        return _.values(pdApi.signalGroups).find(
          driveSignalGroup =>
            (isNew || driveSignalGroup.id !== editedDriveSignalGroup.id) &&
            driveSignalGroup.enumValue === editedDriveSignalGroup.enumValue
        )
          ? false
          : true;
      case driveSignalGroupFields("enumName"): {
        const { enumName } = editedDriveSignalGroup;
        if (this.isEmptyString(enumName)) {
          return false;
        } else {
          return _.values(pdApi.signalGroups).find(
            driveSignalGroup =>
              (isNew || driveSignalGroup.id !== editedDriveSignalGroup.id) &&
              driveSignalGroup.enumName?.trim().toLowerCase() ===
                editedDriveSignalGroup.enumName?.trim().toLowerCase()
          )
            ? false
            : true;
        }
      }
      case driveSignalGroupFields("description"): {
        const { description } = editedDriveSignalGroup;
        return !this.isEmptyString(description);
      }
      case driveSignalGroupFields("name"): {
        const { name } = editedDriveSignalGroup;
        return !this.isEmptyString(name);
      }
      default:
        return true;
    }
  };

  private isEmptyString = (str: string): boolean => !(!!str.trim() && str.trim().length > 0);

  private isAllFieldsValid = (): boolean => {
    const { editedDriveSignalGroup } = this.props;
    const temp = _.keys(editedDriveSignalGroup);
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedDriveSignalGroup: state.editView.editedObject,
  storedDriveSignalGroup: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser,
  isShortenTextEnabled: state.main.isShortenTextEnabled
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditDriveSignalGroup);


